import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const CustomersAdd = () => {
  const navigate = useNavigate();

  // form fields
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [redirect_url, setRedirectUrl] = useState("");
  const [email, setEmail] = useState("");
  const [packageId, setPackageId] = useState("");

  // errors
  const [nameErr, setNameErr] = useState("");
  const [domainErr, setDomainErr] = useState("");
  const [redirect_urlErr, setRedirectUrlErr] = useState("");
  const [emailValidationErrors, setEmailValidationErrors] = useState("");
  const [packageErr, setPackageErr] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ui state
  const [showLoading, setShowLoading] = useState(false);

  // packages list
  const [packages, setPackages] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgError, setPkgError] = useState("");

  // load packages for dropdown
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setPkgLoading(true);
        setPkgError("");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/packages/all`, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            authorization:
              "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
          }),
        });
        const data = await res.json();
        setPackages(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        console.log(e);
        setPkgError("Failed to load packages.");
      } finally {
        setPkgLoading(false);
      }
    };
    loadPackages();
  }, []);

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // reset top-level errors
    setErrorMessage("");
    setNameErr("");
    setDomainErr("");
    setRedirectUrlErr("");
    setEmailValidationErrors("");
    setPackageErr("");

    // quick client-side check for package
    if (!packageId) {
      setPackageErr("Please select a package.");
      return;
    }

    try {
      setShowLoading(true);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/customers/add`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          authorization:
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
        }),
        body: JSON.stringify({
          name,
          domain,
          redirect_url,
          email,
          package_id: Number(packageId),
        }),
      });

      const responseData = await res.json();
      setShowLoading(false);

      if (res.status === 200 || res.status === 201) {
        navigate("/customers");
        return;
      }

      if (res.status === 400) {
        setErrorMessage(responseData.message || "Bad request.");
        return;
      }

      if (res.status === 422 && Array.isArray(responseData.errors)) {
        const getMsg = (prop) => {
          const found = responseData.errors.find((e) => e.property === prop);
          if (found && found.constraints) {
            const vals = Object.values(found.constraints);
            return vals && vals.length ? vals[0] : "";
          }
          return "";
        };
        setNameErr(getMsg("name"));
        setDomainErr(getMsg("domain"));
        setRedirectUrlErr(getMsg("redirect_url"));
        setEmailValidationErrors(getMsg("email"));
        setPackageErr(getMsg("package_id"));
        return;
      }

      setErrorMessage(responseData.message || "Failed to create customer.");
    } catch (err) {
      console.log(err);
      setShowLoading(false);
      setErrorMessage("Network error. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar: back on left, title centered */}
      <div className="w-full bg-blue-600 text-white">
        <div className="relative max-w-5xl mx-auto px-4 py-3 flex items-center justify-center">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm font-medium"
            type="button"
          >
            ← Back
          </button>
          <h1 className="text-lg md:text-xl font-semibold">Create New Customer</h1>
        </div>
      </div>

      {/* Form card */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mx-auto w-full max-w-lg rounded-2xl bg-white shadow-md ring-1 ring-black/5">
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                placeholder="Name"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  nameErr ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                }`}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {nameErr ? <p className="mt-1 text-xs text-red-600">{nameErr}</p> : null}
            </div>

            {/* Domain */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain Name</label>
              <input
                type="text"
                placeholder="https://example.com"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  domainErr ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                }`}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
              {domainErr ? <p className="mt-1 text-xs text-red-600">{domainErr}</p> : null}
            </div>

            {/* Redirect URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Redirect URL</label>
              <input
                type="text"
                placeholder="https://example.com/sso-signin"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  redirect_urlErr ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                }`}
                value={redirect_url}
                onChange={(e) => setRedirectUrl(e.target.value)}
              />
              {redirect_urlErr ? <p className="mt-1 text-xs text-red-600">{redirect_urlErr}</p> : null}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                placeholder="name@company.com"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  emailValidationErrors ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                }`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailValidationErrors ? (
                <p className="mt-1 text-xs text-red-600">{emailValidationErrors}</p>
              ) : null}
            </div>

            {/* Package */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Package</label>
              <select
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  packageErr || pkgError ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                }`}
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                disabled={pkgLoading || !!pkgError}
              >
                <option value="" disabled>
                  {pkgLoading ? "Loading packages..." : "Choose a package"}
                </option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {(pkgError || packageErr) ? (
                <p className="mt-1 text-xs text-red-600">{pkgError || packageErr}</p>
              ) : null}
            </div>

            {/* Global error */}
            {errorMessage ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {errorMessage}
              </div>
            ) : null}

            {/* Actions */}
            <div className="pt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={showLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                // className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white ${
                //   showLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                // }`}
                 className="inline-flex items-center rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-700"
                disabled={showLoading}
              >
                {showLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Creating…
                  </span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomersAdd;
